/**
 * Folder (Group) cache reconcile — pure core.
 *
 * All four folder surfaces (Assets, Screens, Playlists, Schedules) render the SAME
 * `groups` connection of `__typename: 'Group'` nodes, sorted SERVER-side. When a
 * folder is created / renamed / deleted we want to reconcile just that ONE node into
 * the already-loaded, already-sorted edges — insert / update / remove in place —
 * instead of tearing down the paginated connection with a full refetch (which
 * re-drains the background fill and briefly truncates the list; see
 * claude-dev/2026-07-22-folder-cache-reconcile).
 *
 * This module is Apollo-agnostic and pure so it can be unit-tested directly; the
 * cache adapter (reconcileInCache.ts) reads node fields via `readField` and
 * delegates the ordering decisions here.
 */

/** Canonical folder sort fields (server sorts on these). */
export type FolderSortField = 'name' | 'createdAt' | 'lastUpdatedDate';
export type FolderSortDirection = 'ASC' | 'DESC';

export interface FolderSort {
  field: FolderSortField;
  direction: FolderSortDirection;
}

/** Minimal node shape needed for ordering + identity. */
export interface FolderNodeLike {
  _id: string;
  name?: string | null;
  createdAt?: string | null;
  lastUpdatedDate?: string | null;
}

export interface FolderEdgeLike<TNode extends FolderNodeLike = FolderNodeLike> {
  cursor?: string;
  node: TNode;
  __typename?: string;
}

/**
 * Map a feature's raw sort field to the canonical Group field the server orders by.
 * Screens sorts device fields that map onto Group fields (GROUP_SORT_MAP in useScreens);
 * assets uses `originalFileName` for the folder name. Unknown fields fall back to `name`.
 */
export function mapGroupSortField(raw: string): FolderSortField {
  switch (raw) {
    case 'name':
    case 'originalFileName':
    case 'deviceName':
      return 'name';
    case 'createdAt':
    case 'creationDate':
      return 'createdAt';
    case 'lastUpdatedDate':
    case 'currentSelectionDate':
      return 'lastUpdatedDate';
    default:
      return 'name';
  }
}

function nameKey(node: FolderNodeLike): string {
  return (node.name ?? '').toString();
}

function dateValue(value: string | null | undefined): number {
  if (!value) return 0;
  const t = Date.parse(value);
  return Number.isNaN(t) ? 0 : t;
}

/** Byte-order (UTF-16 code-unit) string compare — mirrors MongoDB's default sort. */
function byteCompare(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Ascending comparison of two folder nodes on the given field.
 *
 * Name comparison MUST match the server: the backend sorts `name` with a plain
 * MongoDB `.sort()` and NO collation (base.service — the `.collation({locale:'en'})`
 * path is disabled), i.e. binary/byte order — case-sensitive and NON-numeric. Using
 * locale/numeric compare here disagrees with the server for names like "x.4" vs "x.22"
 * or mixed case, so the optimistic insert lands at the wrong index and the row visibly
 * jumps once the authoritative server data settles. Dates compare by parsed timestamp.
 * Ties break on `_id` (byte order) for a stable, deterministic position.
 */
function compareAsc(a: FolderNodeLike, b: FolderNodeLike, field: FolderSortField): number {
  let cmp = 0;
  if (field === 'name') {
    cmp = byteCompare(nameKey(a), nameKey(b));
  } else {
    cmp = dateValue(a[field]) - dateValue(b[field]);
  }
  if (cmp !== 0) return cmp;
  // Stable tie-break — keeps insert position deterministic for equal keys.
  return byteCompare(a._id, b._id);
}

/**
 * Compare two folder nodes honoring sort direction. Negative → `a` sorts before `b`.
 * Best-effort mirror of the server order; a background sync corrects any drift, so
 * exactness of collation here is not load-bearing.
 */
export function compareFolderNodes(
  a: FolderNodeLike,
  b: FolderNodeLike,
  { field, direction }: FolderSort,
): number {
  const asc = compareAsc(a, b, field);
  return direction === 'DESC' ? -asc : asc;
}

/** Where `node` belongs in an already-sorted edge list (index to splice before). */
export function findInsertIndex<TNode extends FolderNodeLike>(
  edges: ReadonlyArray<FolderEdgeLike<TNode>>,
  node: FolderNodeLike,
  sort: FolderSort,
): number {
  for (let i = 0; i < edges.length; i++) {
    if (compareFolderNodes(node, edges[i].node, sort) < 0) return i;
  }
  return edges.length;
}

export interface ReconcileOp<TNode extends FolderNodeLike> {
  /** Node to insert (if absent) or update (if already present) by `_id`. */
  upsertNode?: TNode;
  /** `_id` to remove from the list. */
  removeId?: string;
}

export interface ReconcileResult<TNode extends FolderNodeLike> {
  edges: Array<FolderEdgeLike<TNode>>;
  /** Net change to apply to the connection total: +1 insert, -1 remove, 0 otherwise. */
  totalDelta: number;
  /** True when the edge array actually changed (skip a cache write otherwise). */
  changed: boolean;
}

function makeEdge<TNode extends FolderNodeLike>(node: TNode): FolderEdgeLike<TNode> {
  // Synthetic cursor — the fill paginates from `pageInfo.endCursor`, never edge
  // cursors, so a placeholder is safe and keeps the edge shape intact.
  return { cursor: `__reconciled__:${node._id}`, node };
}

/**
 * Reconcile a single folder change into an already-sorted edge list.
 * Pure: returns a new array, never mutates the input.
 *
 * - `removeId`: drop the matching edge (total -1).
 * - `upsertNode` present in list: replace its node in place (total 0, no reorder —
 *   a rename that changes sort position is corrected by the background sync).
 * - `upsertNode` absent: splice a new edge at its sorted position (total +1).
 */
export function reconcileEdges<TNode extends FolderNodeLike>(
  edges: ReadonlyArray<FolderEdgeLike<TNode>>,
  op: ReconcileOp<TNode>,
  sort: FolderSort,
): ReconcileResult<TNode> {
  if (op.removeId != null) {
    const next = edges.filter((e) => e.node._id !== op.removeId);
    const changed = next.length !== edges.length;
    return { edges: next, totalDelta: changed ? -1 : 0, changed };
  }

  const node = op.upsertNode;
  if (!node) return { edges: [...edges], totalDelta: 0, changed: false };

  const existingIndex = edges.findIndex((e) => e.node._id === node._id);
  if (existingIndex >= 0) {
    // Update in place — preserve position; background sync repositions if needed.
    const next = edges.slice();
    next[existingIndex] = { ...next[existingIndex], node };
    return { edges: next, totalDelta: 0, changed: true };
  }

  const insertAt = findInsertIndex(edges, node, sort);
  const next = edges.slice();
  next.splice(insertAt, 0, makeEdge(node));
  return { edges: next, totalDelta: 1, changed: true };
}
